/**
 * Note: Before developing this content,
 * you must first define the theme token values the component.
 */
import { generateDynamicThemeToken } from '../../ThemeProvider';
import { getDynamicThemeToken } from './theme';
import { randomStr } from '../../Shared';
import { buttonClasses } from '../../Button';
const componentName = 'Banner';
const prefixName = `ui-${componentName}`;
/**
 * The classes here are the independent classes of the react-ui component.
 * It is mainly isolated from the classes of the mui component.
 * Avoid style conflicts when mui and react-ui are used simultaneously in project applications.
 *
 * On the way to develop react-ui components based on mui,
 * you must use this react-ui classes instead of mui classes.
 * You can control the built-in classes of the mui component through the classes prop of mui component.
 */
export const bannerClasses = {
    // It is recommended that the root classname of the react-ui component be added with a random string.
    // This is mainly to avoid classname naming conflicts in the application
    root: `${prefixName}-root-${randomStr()}`,
    info: `${prefixName}-info`,
    success: `${prefixName}-success`,
    warn: `${prefixName}-warn`,
    warning: `${prefixName}-warning`,
    error: `${prefixName}-error`,
    noIcon: `${prefixName}-no-icon`,
    icon: `${prefixName}-icon`,
    iconWrapper: `${prefixName}-icon-wrapper`,
    iconInfo: `${prefixName}-icon-info`,
    iconSuccess: `${prefixName}-icon-success`,
    iconWarn: `${prefixName}-icon-warn`,
    iconError: `${prefixName}-icon-error`,
    message: `${prefixName}-message`,
    context: `${prefixName}-context`,
    title: `${prefixName}-title`,
    content: `${prefixName}-content`,
    action: `${prefixName}-action`,
    actionWrapper: `${prefixName}-action-btn-group`,
    actionBtn: `${prefixName}-action-btn`,
    okBtn: `${prefixName}-ok-btn`,
    cancelBtn: `${prefixName}-cancel-btn`,
    closeBtn: `${prefixName}-close-btn`,
    closeWrapper: `${prefixName}-close-wrapper`,
    inline: `${prefixName}-inline`,
    bordered: `${prefixName}-bordered`,
    middle: `${prefixName}-middle`,
    closable: `${prefixName}-closable`,
};
/**
 * All style css codes of react-ui components are centrally managed in this method through classes.
 * Through this method, the light and dark themes of the components can be controlled.
 * You don't need to care about the implementation of theme switching.
 * You only need to define the specified style value based on the content of the token.
 */
export function getStyle(theme) {
    // getDynamicThemeToken It can dynamically control the semantic design token of the current component.
    // It is a dynamtic theme token to control two themes (light and dark) of this component.
    const dynamicToken = getDynamicThemeToken(theme);
    // generateDynamicThemeToken will return two contents, one is token data, and the other is tk method.
    // The token includes the light and dark theme style values defined by this component,
    // and it will automatically switch the token content of light or dark according to the outer ThemeProvider.
    // The role of tk is to process custom theme configuration,
    // and override the application custom token to the default light or dark token.
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const style = {
        [`&.${bannerClasses.root}`]: {
            fontFamily: tk(token.fontFamily),
            display: 'flex',
            boxSizing: 'border-box',
            minHeight: tk(token.minHeight),
            padding: 16,
            background: tk(token.background),
            lineHeight: 1,
            fontSize: 18,
            position: 'relative',
            border: 'none',
            borderRadius: '0px',
            [`&.${bannerClasses.inline}`]: {
                borderRadius: '8px',
            },
            [`&.${bannerClasses.bordered}`]: {
                border: '1px solid',
            },
            [`&.${bannerClasses.info}`]: {
                background: tk(token.info.background),
                borderColor: tk(token.info.borderColor),
            },
            [`&.${bannerClasses.success}`]: {
                background: tk(token.success.background),
                borderColor: tk(token.success.borderColor),
            },
            [`&.${bannerClasses.warn}, &.${bannerClasses.warning}`]: {
                background: tk(token.warn.background),
                borderColor: tk(token.warn.borderColor),
            },
            [`&.${bannerClasses.error}`]: {
                background: tk(token.error.background),
                borderColor: tk(token.error.borderColor),
            },
            [`&.${bannerClasses.noIcon}`]: {
                [`.${bannerClasses.iconWrapper}`]: {
                    display: 'none',
                },
            },
            [`.${bannerClasses.iconWrapper}`]: {
                paddingTop: 0,
                paddingBottom: 0,
                width: '20px',
                opacity: 1,
                marginInlineEnd: '8px',
            },
            [`.${bannerClasses.icon}`]: {
                display: 'inline-flex',
                fontSize: '20px',
                width: '20px',
                height: '20px',
                boxSizing: 'border-box',
                alignContent: 'center',
                justifyContent: 'center',
                svg: {
                    display: 'inline-flex',
                },
                [`&.${bannerClasses.iconInfo}`]: {
                    color: tk(token.info.iconColor),
                },
                [`&.${bannerClasses.iconSuccess}`]: {
                    color: tk(token.success.iconColor),
                },
                [`&.${bannerClasses.iconWarn}`]: {
                    fontSize: '20px',
                    color: tk(token.warn.iconColor),
                },
                [`&.${bannerClasses.iconError}`]: {
                    color: tk(token.error.iconColor),
                },
            },
            [`.${bannerClasses.message}`]: {
                fontSize: '14px',
                color: tk(token.color),
                lineHeight: '20px',
                width: '100%',
                minHeight: '20px',
                margin: 0,
                fontWeight: '400',
                textAlign: 'left',
                boxSizing: 'border-box',
                // paddingRight: '16px',
                overflow: 'visible',
                padding: 0,
            },
            [`.${bannerClasses.title}`]: {
                fontFamily: tk(token.fontFamily),
                fontSize: tk(token.title.fontSize),
                color: tk(token.color),
                fontWeight: tk(token.title.fontWeight),
                marginTop: 0,
                marginBottom: '4px',
                lineHeight: tk(token.title.lineHeight),
                boxSizing: 'border-box',
                paddingRight: '0px',
            },
            [`.${bannerClasses.content}`]: {
                fontSize: '14px',
                color: tk(token.color),
                lineHeight: '20px',
                width: '100%',
                margin: 0,
                fontWeight: '400',
                textAlign: 'left',
                boxSizing: 'border-box',
                paddingRight: '0px',
            },
            [`.${bannerClasses.actionWrapper}`]: {
                marginTop: '16px',
                textAlign: 'right',
                [`.${bannerClasses.actionBtn}`]: {
                    marginInlineStart: '8px',
                    minWidth: '68px',
                    '&:first-of-type': {
                        marginInlineStart: 0,
                    },
                },
            },
            [`.${bannerClasses.closeWrapper}`]: {
                position: 'absolute',
                right: '12px',
                top: '14px',
            },
            [`.${bannerClasses.closeBtn}`]: {
                margin: 0,
                [`&.${buttonClasses.iconButton}.${buttonClasses.small}`]: {
                    svg: {
                        fontSize: '12px',
                    },
                },
            },
            // style for middle variant
            [`&.${bannerClasses.middle}`]: {
                [`.${bannerClasses.iconWrapper}`]: {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                },
                [`.${bannerClasses.message}`]: {
                    display: 'flex',
                    flexDirection: 'row',
                },
                [`.${bannerClasses.context}`]: {
                    flex: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                },
                [`.${bannerClasses.title}`]: {
                    paddingRight: '0px',
                },
                [`.${bannerClasses.content}`]: {
                    paddingRight: '0px',
                },
                [`.${bannerClasses.closeWrapper}`]: {
                    position: 'unset',
                    right: 'unset',
                    top: 'unset',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginInlineStart: '4px',
                },
                [`.${bannerClasses.actionWrapper}`]: {
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: '20px',
                },
                [`.${bannerClasses.actionBtn}`]: {
                    margin: 0,
                },
                [`.${bannerClasses.cancelBtn}`]: {
                    display: 'none',
                },
            },
            // style for closable
            [`&.${bannerClasses.closable}`]: {
                [`.${bannerClasses.title}`]: {
                    paddingRight: '20px',
                },
                [`.${bannerClasses.content}`]: {
                    paddingRight: '20px',
                },
                [`&.${bannerClasses.middle}`]: {
                    [`.${bannerClasses.title}`]: {
                        paddingRight: '0px',
                    },
                    [`.${bannerClasses.content}`]: {
                        paddingRight: '0px',
                    },
                },
            },
        },
    };
    return style;
}
